import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@/context/user-context';
import type * as Types from '../../../graphql/types';
import type { CreateReportSubscriptionInput } from '../../../graphql/types';
import { useGetHotelTeamForRecipientsQuery } from '../gql/_gen_/report-subscriptions.gql';
import {
  DEFAULT_PREFERENCES,
  DELIVERY_TIME_OPTIONS,
  PRIORITY_OPTIONS,
  TIMEZONE_OPTIONS,
} from '../hooks/use-subscription-management';
import { clearFieldError, validateSubscriptionForm } from '../utils';
import { HotelTeamRecipientsSelect } from './hotel-team-recipients-select';
import { RmUserSelect } from './rm-user-select';

interface ReportSubscriptionFormProps {
  brandCode?: string;
  initialData?: Types.ReportSubscription;
  onSubmit: (data: Types.CreateReportSubscriptionInput) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

export const ReportSubscriptionForm: React.FC<ReportSubscriptionFormProps> = ({
  brandCode: propBrandCode,
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  title = 'Automated Report Subscription',
  description = 'Configure your automated report delivery preferences',
}) => {
  const { user } = useUser();

  // Form state
  const [brandCode, setBrandCode] = useState(propBrandCode || initialData?.brandCode || '');
  const [rmUserId, setRmUserId] = useState<number | undefined>(initialData?.rmUserId ?? undefined);
  const [timezone, setTimezone] = useState(initialData?.timezone || 'America/Los_Angeles');
  const [preferences, setPreferences] = useState<Types.ReportSubscriptionPreferencesInput>(
    initialData?.preferences || DEFAULT_PREFERENCES,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Query hotel team to validate recipients exist
  const normalizedBrandCode = brandCode.trim().toUpperCase();
  const canQueryHotelTeam = normalizedBrandCode.length === 5;
  const { data: hotelTeamData, loading: hotelTeamLoading } = useGetHotelTeamForRecipientsQuery({
    variables: { brandCode: normalizedBrandCode },
    skip: !canQueryHotelTeam,
  });
  const hotelTeamMembers = hotelTeamData?.getHotelTeam ?? [];
  const hasNoActiveTeamMembers =
    canQueryHotelTeam && !hotelTeamLoading && hotelTeamMembers.length === 0;

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setBrandCode(initialData.brandCode);
      setRmUserId(initialData.rmUserId);
      setTimezone(initialData.timezone);
      setPreferences(initialData.preferences);
    }
  }, [initialData]);

  // Default RM user to signed-in user for new subscriptions
  useEffect(() => {
    if (initialData) return;
    if (rmUserId !== undefined) return;
    const parsed = Number.parseInt(String(user?.id ?? ''), 10);
    if (Number.isFinite(parsed)) {
      setRmUserId(parsed);
    }
  }, [user?.id, initialData, rmUserId]);

  // Validation using shared utility
  const validateForm = (): boolean => {
    const validationErrors = validateSubscriptionForm(
      brandCode,
      timezone,
      preferences,
      !propBrandCode, // Only validate brand code if not provided as prop (hotel context)
    );

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData: CreateReportSubscriptionInput = {
      brandCode: brandCode.toUpperCase(),
      ...(rmUserId !== undefined ? { rmUserId } : {}),
      timezone,
      preferences,
    };

    await onSubmit(formData);
  };

  const handlePreferenceChange = (
    key: keyof Types.ReportSubscriptionPreferencesInput,
    value: boolean | string | string[] | number | null,
  ) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear errors when user makes changes
    if (
      key === 'includeMonday' ||
      key === 'includeTuesday' ||
      key === 'includeWednesday' ||
      key === 'includeThursday' ||
      key === 'includeFriday' ||
      key === 'includeSaturday' ||
      key === 'includeSunday'
    ) {
      clearFieldError(errors, setErrors, 'days');
    }
    if (key === 'deliveryTime') {
      clearFieldError(errors, setErrors, 'deliveryTime');
    }
    if (key === 'startDate' || key === 'endDate') {
      clearFieldError(errors, setErrors, 'dateRange');
    }
  };

  const handleBrandCodeChange = (value: string) => {
    setBrandCode(value.toUpperCase());
    clearFieldError(errors, setErrors, 'brandCode');
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    clearFieldError(errors, setErrors, 'timezone');
  };

  const handleDeliveryTimeChange = (value: string) => {
    handlePreferenceChange('deliveryTime', value);
  };

  return (
    <Card className='w-full max-w-md'>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className='space-y-4'>
          {/* Revenue Manager */}
          <div className='space-y-2'>
            <Label htmlFor='rmUserId'>Revenue Manager</Label>
            <RmUserSelect value={rmUserId} onChange={setRmUserId} disabled={loading} />
            <p className='text-xs text-muted-foreground'>Defaults to the signed-in user</p>
          </div>

          {/* Brand Code */}
          <div className='space-y-2'>
            <Label htmlFor='brandCode'>
              Brand Code
              {propBrandCode && (
                <span className='ml-2 text-xs text-muted-foreground'>(Current Hotel)</span>
              )}
            </Label>
            <Input
              id='brandCode'
              value={brandCode}
              onChange={(e) => handleBrandCodeChange(e.target.value)}
              placeholder='Enter 5-character brand code'
              maxLength={5}
              className={errors.brandCode ? 'border-red-500' : ''}
              disabled={loading || !!propBrandCode} // Disable when in hotel context
              readOnly={!!propBrandCode} // Make read-only when in hotel context
            />
            {propBrandCode && (
              <p className='text-xs text-muted-foreground'>
                This subscription will be created for the current hotel ({propBrandCode})
              </p>
            )}
            {errors.brandCode && <p className='text-sm text-red-500'>{errors.brandCode}</p>}
          </div>

          {/* Timezone */}
          <div className='space-y-2'>
            <Label htmlFor='timezone'>Timezone</Label>
            <Select value={timezone} onValueChange={handleTimezoneChange} disabled={loading}>
              <SelectTrigger className={errors.timezone ? 'border-red-500' : ''}>
                <SelectValue placeholder='Select timezone' />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.timezone && <p className='text-sm text-red-500'>{errors.timezone}</p>}
          </div>

          {/* Days */}
          <div className='space-y-3'>
            <Label>Days to Receive Reports</Label>
            <div className='grid grid-cols-2 gap-2'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='monday'
                  checked={preferences.includeMonday}
                  onCheckedChange={(checked) => handlePreferenceChange('includeMonday', !!checked)}
                  disabled={loading}
                />
                <Label htmlFor='monday' className='text-sm font-normal'>
                  Monday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='tuesday'
                  checked={preferences.includeTuesday}
                  onCheckedChange={(checked) => handlePreferenceChange('includeTuesday', !!checked)}
                  disabled={loading}
                />
                <Label htmlFor='tuesday' className='text-sm font-normal'>
                  Tuesday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='wednesday'
                  checked={preferences.includeWednesday}
                  onCheckedChange={(checked) =>
                    handlePreferenceChange('includeWednesday', !!checked)
                  }
                  disabled={loading}
                />
                <Label htmlFor='wednesday' className='text-sm font-normal'>
                  Wednesday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='thursday'
                  checked={preferences.includeThursday}
                  onCheckedChange={(checked) =>
                    handlePreferenceChange('includeThursday', !!checked)
                  }
                  disabled={loading}
                />
                <Label htmlFor='thursday' className='text-sm font-normal'>
                  Thursday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='friday'
                  checked={preferences.includeFriday}
                  onCheckedChange={(checked) => handlePreferenceChange('includeFriday', !!checked)}
                  disabled={loading}
                />
                <Label htmlFor='friday' className='text-sm font-normal'>
                  Friday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='saturday'
                  checked={preferences.includeSaturday}
                  onCheckedChange={(checked) =>
                    handlePreferenceChange('includeSaturday', !!checked)
                  }
                  disabled={loading}
                />
                <Label htmlFor='saturday' className='text-sm font-normal'>
                  Saturday
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='sunday'
                  checked={preferences.includeSunday}
                  onCheckedChange={(checked) => handlePreferenceChange('includeSunday', !!checked)}
                  disabled={loading}
                />
                <Label htmlFor='sunday' className='text-sm font-normal'>
                  Sunday
                </Label>
              </div>
            </div>
            {errors.days && <p className='text-sm text-red-500'>{errors.days}</p>}
          </div>

          {/* AI Notes */}
          <div className='space-y-2'>
            <Label>AI Notes</Label>
            <div className='flex items-center space-x-2'>
              <Checkbox
                id='useAiNotes'
                checked={preferences.useAiNotes}
                onCheckedChange={(checked) => handlePreferenceChange('useAiNotes', !!checked)}
                disabled={loading}
              />
              <Label htmlFor='useAiNotes' className='text-sm font-normal'>
                Include AI-generated notes
              </Label>
            </div>
          </div>

          {/* Subscription Description */}
          <div className='space-y-2'>
            <Label htmlFor='subscriptionDescription'>Subscription Description (Optional)</Label>
            <Input
              id='subscriptionDescription'
              value={preferences.description ?? ''}
              onChange={(e) => handlePreferenceChange('description', e.target.value || null)}
              placeholder='e.g. Weekend Reports, Vacation Sender'
              disabled={loading}
            />
            <p className='text-xs text-muted-foreground'>Used as a label in the UI</p>
          </div>

          {/* Recipients */}
          <div className='space-y-2'>
            <Label>Recipients (Optional)</Label>
            <HotelTeamRecipientsSelect
              brandCode={brandCode}
              value={preferences.recipientTeamMemberIds}
              onChange={(ids) => handlePreferenceChange('recipientTeamMemberIds', ids)}
              disabled={loading}
            />
            <p className='text-xs text-muted-foreground'>Select existing Hotel Team members</p>
          </div>

          {/* Date Range */}
          <div className='space-y-2'>
            <Label>Date Range (Optional)</Label>
            <div className='grid grid-cols-2 gap-2'>
              <div className='space-y-1'>
                <Label htmlFor='startDate' className='text-xs text-muted-foreground'>
                  Start Date
                </Label>
                <Input
                  id='startDate'
                  type='date'
                  value={preferences.startDate ?? ''}
                  onChange={(e) =>
                    handlePreferenceChange('startDate', e.target.value ? e.target.value : null)
                  }
                  disabled={loading}
                />
              </div>
              <div className='space-y-1'>
                <Label htmlFor='endDate' className='text-xs text-muted-foreground'>
                  End Date
                </Label>
                <Input
                  id='endDate'
                  type='date'
                  value={preferences.endDate ?? ''}
                  onChange={(e) =>
                    handlePreferenceChange('endDate', e.target.value ? e.target.value : null)
                  }
                  disabled={loading}
                />
              </div>
            </div>
            {errors.dateRange && <p className='text-sm text-red-500'>{errors.dateRange}</p>}
          </div>

          {/* Message */}
          <div className='space-y-2'>
            <Label htmlFor='message'>Message (Optional)</Label>
            <Textarea
              id='message'
              value={preferences.message ?? ''}
              onChange={(e) => handlePreferenceChange('message', e.target.value || null)}
              placeholder='Add a note to include with the report email'
              disabled={loading}
            />
          </div>

          {/* Delivery Time */}
          <div className='space-y-2'>
            <Label htmlFor='deliveryTime'>Delivery Time</Label>
            <Select
              value={preferences.deliveryTime}
              onValueChange={handleDeliveryTimeChange}
              disabled={loading}
            >
              <SelectTrigger className={errors.deliveryTime ? 'border-red-500' : ''}>
                <SelectValue placeholder='Select delivery time' />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_TIME_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.deliveryTime && <p className='text-sm text-red-500'>{errors.deliveryTime}</p>}
          </div>

          {/* Priority */}
          <div className='space-y-2'>
            <Label htmlFor='priority'>Priority</Label>
            <Select
              value={String(preferences.priority ?? 0)}
              onValueChange={(value) => handlePreferenceChange('priority', Number(value))}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select priority' />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              When multiple subscriptions for the same hotel trigger at the same time, only the
              highest priority (10 = highest) will run.
            </p>
          </div>

          {/* No Active Team Members Warning */}
          {hasNoActiveTeamMembers && (
            <Alert variant='destructive'>
              <AlertTriangle className='h-4 w-4' />
              <AlertDescription>
                There are no active hotel team members for this brand code. At least one active team
                member is required to create an automated subscription.
              </AlertDescription>
            </Alert>
          )}

          {/* Form Actions */}
          <div className='flex justify-end space-x-2 pt-4'>
            {onCancel && (
              <Button type='button' variant='outline' onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            )}
            <Button type='submit' disabled={loading || hasNoActiveTeamMembers}>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              {initialData ? 'Update Subscription' : 'Create Subscription'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
